import { Box, Tab, TablePagination, Tabs, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import LayoutTable from "./layout/Table";
import Search from "../../../shared/components/Search";
import { deleteScreen, getAllScreens } from "../../../services/screen";
import { FilterIconSvg } from "../../../assets/icons/FilterIconSvg";
import { useGlobalContext } from "../../../App";
import ActionCard from "../../../shared/components/ActionCard";
import { toastError, toastSuccess } from "../../../utils/ToastMessage";
import DeleteDialog from "../../products/product/DeleteDialog";
import EditMulipleScreensDialogue from "./components/EditMulipleScreensDialogue";
import EditScreens from "./components/EditScreens";
import { useNavigate } from "react-router-dom";
import FiltersPopup from "../FiltersComponent/FiltersPopup";

type Props = {};

export interface ActionMenuProps {
  title: string;
  action: () => void;
  actionTitle: string;
}

const tableRef = React.createRef<any>();

const ScreensList = (props: Props) => {
  const { showSelectedRowsMenuFooter, setScreenDetailsList } =
    useGlobalContext();
  const navigate = useNavigate();
  const columnItems = [
    { id: "Name", label: "Name" },
    { id: "Category", label: "Category" },
    { id: "Collection", label: "Collection" },
    { id: "License", label: "License" },
    { id: "Status", label: "Status" },
    { id: "Date Edited", label: "Date Edited" },
  ];

  const [isDeleteDialogVisible, setIsDeleteDialogVisible] = useState(false);
  const [filtersPopup, setFiltersPopup] = useState(false);

  // TabPanel handling states and functions
  const [value, setValue] = useState(0);
  const [status, setStatus] = useState("");

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);

    switch (newValue) {
      case 1:
        setStatus("ACTIVE");
        break;
      case 2:
        setStatus("INACTIVE");
        break;
      case 3:
        setStatus("DRAFT");
        break;
      default:
        setStatus("");
        break;
    }
  };

  // current page and rows per page handling states and functions
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Add this for debounce delay
  const DEBOUNCE_DELAY = 700;
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const [lastKeyPressed, setLastKeyPressed] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [searchFilteredProductList, setSearchFilteredProductList] =
    useState<any>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productList, setProductList] = useState<any>([]);
  const [
    fetchAppliedFiltersFromFiltersPopup,
    setFetchAppliedFiltersFromFiltersPopup,
  ] = useState(false);
  const [activeFilters, setActiveFilters] = useState<any>({
    categories: [],
    collections: [],
    licenses: [],
  });
  // console.log("activeFilters",activeFilters)

  // ROWS SELECTION AND ACTION CARD HANDLING STATES
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [actionCardProps, setActionCardProps] = useState<ActionMenuProps>();
  const [activeSelectedMenu, setActiveSelectedMenu] = useState(false);
  const [
    editMultipleScreensDialogueVisible,
    setEditMultipleScreensDialogueVisible,
  ] = useState(false);
  const [editScreensDialogueVisible, setEditScreensDialogueVisible] =
    useState(false);
  const [allData, setAlldata] = useState<any>([]);
  const [totalScreen, setTotalScreen] = useState(null);

  const handleSearch = (value: string) => {
    setSearch(value);
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    if (lastKeyPressed !== " ") {
      debounceTimeoutRef.current = setTimeout(() => {
        // console.log("debounce")
        fetchScreens({
          searchBy: value,
          page: 1,
          pageSize: rowsPerPage,
          categories: activeFilters?.categories || [],
          collections: activeFilters?.collections || [],
          licenses: activeFilters?.licenses || [],
        });
      }, DEBOUNCE_DELAY);
    }
  };

  const handleKeyDown: any = (event: React.KeyboardEvent<HTMLInputElement>) => {
    setLastKeyPressed(event.key);
    if (event.key === " ") {
      clearTimeout(debounceTimeoutRef.current);
      fetchScreens({
        searchBy: search,
        page: 1,
        pageSize: rowsPerPage,
        status,
        categories: activeFilters?.categories || [],
        collections: activeFilters?.collections || [],
        licenses: activeFilters?.licenses || [],
      });
    }
  };

  const handleClearAllFilters = () => {
    setActiveFilters(() => {
      return {
        categories: [],
        licenses: [],
        collections: [],
      };
    });
  };

  const fetchAll = async (filters?: any) => {
    await getAllScreens(filters) // changeLater
      .then((res: any) => {
        setAlldata(res);
      })
      .catch((err) => {
        setProductsLoading(false);
      });
  };
  const fetchAllSearchFilteredScreen = async (filters?: any) => {
    await getAllScreens(filters) // changeLater
      .then((res: any) => {
        setSearchFilteredProductList(res);
      })
      .catch((err) => {
        setProductsLoading(false);
      });
  };

  const fetchScreens = async (filters?: any) => {
    setProductsLoading(true);
    // console.log("filters",filters)
    await getAllScreens(filters) // changeLater
      .then((res: any) => {
        setProductsLoading(false);
        setProductList(res);
        setTotalScreen(res?.totalScreens); // changeLater
      })
      .catch((err) => {
        console.log(err);
        setProductsLoading(false);
      });
  };

  const handleRefreshData = () => {
    setPage(0);
    fetchScreens({
      searchBy: search,
      page: page,
      pageSize: rowsPerPage,
      status,
      categories: activeFilters?.categories || [],
      collections: activeFilters?.collections || [],
      licenses: activeFilters?.licenses || [],
    });
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);

    const newFilterList = {
      searchBy: search,
      page: newPage + 1,
      pageSize: rowsPerPage,
      status,
      categories: activeFilters?.categories || [],
      collections: activeFilters?.collections || [],
      licenses: activeFilters?.licenses || [],
    };
    fetchScreens(newFilterList);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    tableRef.current && tableRef.current.scrollIntoView();
    setRowsPerPage(+event.target.value);
    setPage(0);

    const newFilterList = {
      searchByTitle: search,
      page: 1,
      pageSize: +event.target.value,
      status,
      categories: activeFilters?.categories || [],
      collections: activeFilters?.collections || [],
      licenses: activeFilters?.licenses || [],
    };
    fetchScreens(newFilterList);
  };

  const handleChangeSelectedMenu = (value: any) => {
    setActionCardProps({
      title: value.title,
      action: value.action,
      actionTitle: value.actionTitle,
    });
  };

  const handleDelete = async () => {
    try {
      const failedDeletedRows: any = [];

      await Promise.all(
        selectedRows?.map(async (row: any) => {
          try {
            await deleteScreen(row); // changeLater
          } catch (err: any) {
            failedDeletedRows?.push(row);
          }
        })
      );

      if (failedDeletedRows.length === 0) {
        toastSuccess(
          "Successfully Deleted!",
          "Screens are deleted successfully."
        );
      } else {
        toastError(
          "Ops! Something went wrong.",
          `Failed deleting screens. ${failedDeletedRows.map(
            (row: any, index: number) =>
              `${row}${index !== failedDeletedRows.length - 1 ? "," : ""}`
          )}`
        );
      }

      // Common logic to reset after deletion attempt
      setSelectedRows([]);
      handleRefreshData();
      setIsDeleteDialogVisible(false);
    } catch (error) {
      toastError("Ops! Something went wrong.", "Failed deleting the screen.");
      setSelectedRows([]);
      handleRefreshData();
      setIsDeleteDialogVisible(false);
    }
  };

  const handleEditMultipleScreens = () => {
    setEditMultipleScreensDialogueVisible(true);
  };

  const handleEditScreens = () => {
    //We have to get all screens with ids matching to rows selected and
    const selectedRowsObject: any = {};
    for (let row of selectedRows) {
      selectedRowsObject[row] = true; //We could use a set but I'm forgetting its operations
    }

    const screensArr: any = [];
    // for (let page of productList?.paginatedPages) {
    //   if (selectedRowsObject[page?.id]) {
    //     screensArr.push(page);
    //   }
    // }
    const forSelectDataScreens =
      search == "" ? allData : searchFilteredProductList;
    // for (let page of forSelectDataScreens?.paginatedPages) { // changeLater
    for (let screen of forSelectDataScreens?.paginatedScreens) {
      // changeLater
      if (selectedRowsObject[screen?.id]) {
        screensArr.push(screen);
      }
    }

    // Save current filter state in sessionStorage
    const currentFilters = {
      search,
      page,
      rowsPerPage,
      status,
      activeFilters,
    };
    sessionStorage.setItem("screenFilters", JSON.stringify(currentFilters));

    setScreenDetailsList(screensArr);
    // navigate("page/multiple-edit", { replace: true });
    navigate("../multiple-edit", { replace: true });
  };

  const handleCloseFiltersPopup = () => {
    setFiltersPopup(false);
  };

  useEffect(() => {
    // Load saved filters if they exist
    const savedFilters = sessionStorage.getItem("screenFilters");
    if (savedFilters) {
      const filters = JSON.parse(savedFilters);
      setSearch(filters.search);
      setPage(filters.page);
      setRowsPerPage(filters.rowsPerPage);
      setStatus(filters.status);
      setActiveFilters(
        filters.activeFilters || {
          categories: [],
          collections: [],
          licenses: [],
        }
      );

      fetchScreens({
        searchBy: filters.search,
        page: filters.page,
        pageSize: filters.rowsPerPage,
        status: filters.status,
        categories: filters.activeFilters?.categories || [],
        collections: filters.activeFilters?.collections || [],
        licenses: filters.activeFilters?.licenses || [],
      });
    } else {
      fetchScreens({
        searchByTitle: search,
        page: page,
        pageSize: rowsPerPage,
        status,
        categories: activeFilters?.categories || [],
        collections: activeFilters?.collections || [],
        licenses: activeFilters?.licenses || [],
      });
    }
  }, [value, fetchAppliedFiltersFromFiltersPopup]);

  useEffect(() => {}, [productList]);

  useEffect(() => {
    setActiveSelectedMenu(false);
  }, []);

  useEffect(() => {
    setActiveSelectedMenu(showSelectedRowsMenuFooter);
  }, [showSelectedRowsMenuFooter]);

  useEffect(() => {
    fetchAll({
      pageSize: totalScreen, // changeLater
    });
  }, [productList]);

  useEffect(() => {
    fetchAllSearchFilteredScreen({
      searchByTitle: search,
      page: 1,
      pageSize: totalScreen, // changeLater
    });
  }, [productList, search]);

  useEffect(() => {
    if (selectedRows.length > 0) {
      setActiveSelectedMenu(true);
    } else {
      setActiveSelectedMenu(false);
    }
  }, [selectedRows]);
  return (
    <>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          paddingTop: "32px",
          paddingBottom: "12px",
        }}
      >
        {isDeleteDialogVisible && (
          <DeleteDialog
            title="Are you sure you want to delete the screens ?"
            setIsDeleteDialogVisible={setIsDeleteDialogVisible}
            deleteAction={() => {
              handleDelete();
            }}
          />
        )}
        {filtersPopup && (
          <FiltersPopup
            activeDialog={filtersPopup}
            handleDialog={handleCloseFiltersPopup}
            setProductList={setProductList}
            productList={productList}
            // handleChangeActiveFilters={handleChangeActiveFilters}
            setActiveFilters={setActiveFilters}
            activeFilters={activeFilters}
            handleClearAllFilters={handleClearAllFilters}
            setFetchAppliedFiltersFromFiltersPopup={
              setFetchAppliedFiltersFromFiltersPopup
            }
            fetchAppliedFiltersFromFiltersPopup={
              fetchAppliedFiltersFromFiltersPopup
            }
            currentSearch={search}
            currentPage={page}
            currentRowsPerPage={rowsPerPage}
            currentStatus={status}
          />
        )}
        {editMultipleScreensDialogueVisible && (
          <EditMulipleScreensDialogue
            setEditMultipleScreensDialogueVisible={
              setEditMultipleScreensDialogueVisible
            }
            selectedRows={selectedRows}
            setActiveSelectedMenu={setActiveSelectedMenu}
            setProductList={setProductList}
            setSelectedRows={setSelectedRows}
          />
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            width: "85%",
            mb: "21px",
            gap: "12px",
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Tabs
              value={value}
              onChange={handleChange}
              variant="scrollable"
              scrollButtons="auto"
              indicatorColor="primary"
              TabIndicatorProps={{ style: { display: "none" } }}
              TabScrollButtonProps={{
                style: {
                  color: "white",
                },
              }}
              visibleScrollbar={true}
              sx={{
                borderColor: "divider",
                fontWeight: "500",
                color: "white",
                flex: 1,
                width: "100%",
                minHeight: "fit-content",
                "& .MuiTabs-flexContainer": {
                  display: "flex",
                  flexDirection: "row",
                  gap: "12px",
                  padding: "0px",
                },
              }}
              aria-label="scrollable auto tabs example"
            >
              <Tab
                disableRipple
                sx={{
                  textTransform: "none",
                  fontWeight: "500",
                  color: "white",
                  padding: "0px !important",
                }}
                label={
                  <Box
                    sx={{
                      boxShadow:
                        value === 0
                          ? "inset 0px 0px 0px 1px #CCFF00"
                          : "inset 0px 0px 0px 1px #3D3D3D",
                      bgcolor: value === 0 ? "#CCFF00" : "#141414",
                      color: value === 0 ? "black" : "rgba(255, 255, 255, 0.5)",
                      borderRadius: "100px",
                      minHeight: "46px !important",
                      maxHeight: "46px !important",
                      // width: "121px",
                      width: "67px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      // padding: "0px !important",
                      padding: "12px 0px",
                    }}
                  >
                    <Typography fontWeight={600} variant="body1">
                      {/* Component */}
                      All
                    </Typography>
                  </Box>
                }
              />

              <Tab
                disableRipple
                sx={{
                  textTransform: "none",
                  fontWeight: "500",
                  color: "white",
                  padding: "0px !important",
                }}
                label={
                  <Box
                    sx={{
                      boxShadow:
                        value === 1
                          ? "inset 0px 0px 0px 1px #CCFF00"
                          : "inset 0px 0px 0px 1px #3D3D3D",
                      bgcolor: value === 1 ? "#CCFF00" : "#141414",
                      color: value === 1 ? "black" : "rgba(255, 255, 255, 0.5)",
                      borderRadius: "100px",
                      minHeight: "46px !important",
                      maxHeight: "46px !important",
                      width: "95px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      mr: "0px",
                    }}
                  >
                    <Typography fontWeight={600} variant="body1">
                      Active
                    </Typography>
                  </Box>
                }
              />
              <Tab
                disableRipple
                sx={{
                  textTransform: "none",
                  fontWeight: "500",
                  color: "white",
                  padding: "0px !important",
                }}
                label={
                  <Box
                    sx={{
                      boxShadow:
                        value === 2
                          ? "inset 0px 0px 0px 1px #CCFF00"
                          : "inset 0px 0px 0px 1px #3D3D3D",
                      bgcolor: value === 2 ? "#CCFF00" : "#141414",
                      color: value === 2 ? "black" : "rgba(255, 255, 255, 0.5)",
                      borderRadius: "100px",
                      minHeight: "46px !important",
                      maxHeight: "46px !important",
                      width: "95px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      mr: "0px",
                    }}
                  >
                    <Typography fontWeight={600} variant="body1">
                      Inactive
                    </Typography>
                  </Box>
                }
              />
              <Tab
                disableRipple
                sx={{
                  textTransform: "none",
                  fontWeight: "500",
                  color: "white",
                  padding: "0px !important",
                }}
                label={
                  <Box
                    sx={{
                      boxShadow:
                        value === 3
                          ? "inset 0px 0px 0px 1px #CCFF00"
                          : "inset 0px 0px 0px 1px #3D3D3D",
                      bgcolor: value === 3 ? "#CCFF00" : "#141414",
                      color: value === 3 ? "black" : "rgba(255, 255, 255, 0.5)",
                      borderRadius: "100px",
                      minHeight: "46px !important",
                      maxHeight: "46px !important",
                      width: "95px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      mr: "0px",
                    }}
                  >
                    <Typography fontWeight={600} variant="body1">
                      Draft
                    </Typography>
                  </Box>
                }
              />
            </Tabs>
          </Box>

          <Box
            onKeyDown={handleKeyDown}
            sx={{ display: "flex", height: "52px" }}
          >
            <Search
              searchFormStyles={{
                minHeight: "22px",
              }}
              style={{
                minHeight: "52px",
                color: "white",
              }}
              icon={true}
              placeholder="Search"
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </Box>

          <Box
            onClick={() => {
              setFiltersPopup(true);
            }}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "52px",
              height: "52px",
              borderRadius: "14px",
              bgcolor: "#141414",
              boxShadow: "inset 0px 0px 0px 2px #3D3D3D",
              cursor: "pointer",
            }}
          >
            <FilterIconSvg />
          </Box>
        </Box>
        <Box
          // className="custom-scrollbar"
          sx={{
            width: "100%",
            overflowY: "scroll",
            // overflowX: "scroll",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            scrollbarWidth: "none",
          }}
        >
          <TabPanel value={value} index={0}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                // overflowX: "scroll",
              }}
            >
              <Box
                sx={{
                  width: "85%",
                  paddingBottom: activeSelectedMenu ? "70px" : "0px",
                  height: "auto",
                  // overflowX: "scroll",
                }}
              >
                <LayoutTable
                  loading={productsLoading}
                  // rows={productList?.paginatedPages} // changeLater
                  rows={productList?.paginatedScreens} // changeLater
                  columns={columnItems}
                  handleRefreshData={handleRefreshData}
                  selectedRows={selectedRows}
                  setSelectedRows={setSelectedRows}
                  activeSelectedMenu={activeSelectedMenu}
                  handleSelectedMenu={(values) => {
                    handleChangeSelectedMenu(values);
                  }}
                  allData={allData}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  searchFilteredProductList={searchFilteredProductList}
                  search={search}
                  activeFilters={activeFilters}
                  status={status}
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <TablePagination
                    labelRowsPerPage="Showing"
                    SelectProps={{
                      inputProps: {
                        "aria-label": "rows per page",
                      },
                      style: {
                        fontWeight: "700",
                      },
                      MenuProps: {
                        PaperProps: {
                          sx: {
                            backgroundColor: "#3D3D3D",
                            "& .MuiMenuItem-root": {
                              color: "#ffff",
                              "&:hover": {
                                backgroundColor: "#555555",
                              },
                            },
                          },
                        },
                      },
                    }}
                    sx={{
                      "& .MuiTablePagination-selectLabel": {
                        fontWeight: "700",
                        color: "rgba(255, 255, 255, 0.24)",
                        marginRight: "5px",
                      },
                      "& .MuiTablePagination-displayedRows": {
                        color: "rgba(255, 255, 255, 0.24)",
                        fontWeight: "600",
                      },
                      "& .MuiButtonBase-root": {
                        boxShadow: "inset 0px 0px 0px 2px #3D3D3D",
                        cursor: "pointer !important",
                      },
                      "& .MuiSvgIcon-root": {
                        color: "white",
                      },
                      "& .MuiTablePagination-actions": {
                        display: "flex",
                        gap: "12px !important",
                        flex: 1,
                        justifyContent: "flex-end",
                      },
                      "&.MuiTablePagination-root": {
                        width: "100%",
                        flex: 1,
                        display: "flex",
                        justifyContent: "space-between",
                      },
                      "& .MuiTablePagination-spacer": {
                        flex: 0,
                        display: "none",
                      },
                      "& .MuiTablePagination-toolbar": {
                        flex: 1,
                        display: "flex",
                        paddingLeft: 0,
                      },
                      "& .MuiTablePagination-select": {
                        backgroundColor: "rgba(255, 255, 255, 0.08)",
                        borderRadius: "4px",
                        padding: "6px 12px",
                      },
                      "& .MuiInputBase-input": {
                        color: "#ffff",
                      },
                      // To hide the rows per page dropdown
                      "& .MuiInputBase-root": {
                        // display: "none",
                      },
                    }}
                    rowsPerPageOptions={[25, 50, 100]}
                    component="div"
                    count={productList?.totalScreens} // changeLater
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </Box>

                {/* -------Multi select rows Action bar------- */}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 20,
                    left: "auto",
                    right: "auto",
                    width: "85%",
                    zIndex: 10,
                  }}
                >
                  <ActionCard
                    onNegativeAction={() => {
                      setIsDeleteDialogVisible(true);
                    }}
                    negativeButtonText={"Delete"}
                    headerText={actionCardProps?.title || "No items selected"}
                    isActionCardVisible={activeSelectedMenu}
                    onCloseButtonClick={() => {
                      setActiveSelectedMenu(false);
                      setSelectedRows([]);
                    }}
                    isNegativeButtonVisible={true}
                    isEditMultipleComponentsButtonLoading={false}
                    isEditMultipleComponentsButtonVisible={true}
                    editMultipleComponentsButtonText={"Edit Multiple screens"}
                    onEditMultipleComponentsButtonAction={() => {
                      handleEditMultipleScreens();
                    }}
                    isEditButtonLoading={false}
                    isEditButtonVisible={true}
                    editButtonText={"Edit"}
                    onEditButtonAction={() => {
                      handleEditScreens();
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </TabPanel>
          <TabPanel value={value} index={1}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                // overflowX: "scroll",
              }}
            >
              <Box
                sx={{
                  width: "85%",
                  paddingBottom: activeSelectedMenu ? "70px" : "0px",
                  height: "auto",
                  // overflowX: "scroll",
                }}
              >
                <LayoutTable
                  loading={productsLoading}
                  // rows={productList?.paginatedPages}  // changeLater
                  rows={productList?.paginatedScreens} // changeLater
                  columns={columnItems}
                  handleRefreshData={handleRefreshData}
                  selectedRows={selectedRows}
                  setSelectedRows={setSelectedRows}
                  activeSelectedMenu={activeSelectedMenu}
                  handleSelectedMenu={(values) => {
                    handleChangeSelectedMenu(values);
                  }}
                  allData={allData}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  searchFilteredProductList={searchFilteredProductList}
                  search={search}
                  activeFilters={activeFilters}
                  status={status}
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <TablePagination
                    labelRowsPerPage="Showing"
                    SelectProps={{
                      inputProps: {
                        "aria-label": "rows per page",
                      },
                      style: {
                        fontWeight: "700",
                      },
                      MenuProps: {
                        PaperProps: {
                          sx: {
                            backgroundColor: "#3D3D3D",
                            "& .MuiMenuItem-root": {
                              color: "#ffff",
                              "&:hover": {
                                backgroundColor: "#555555",
                              },
                            },
                          },
                        },
                      },
                    }}
                    sx={{
                      "& .MuiTablePagination-selectLabel": {
                        fontWeight: "700",
                        color: "rgba(255, 255, 255, 0.24)",
                        marginRight: "5px",
                      },
                      "& .MuiTablePagination-displayedRows": {
                        color: "rgba(255, 255, 255, 0.24)",
                        fontWeight: "600",
                      },
                      "& .MuiButtonBase-root": {
                        boxShadow: "inset 0px 0px 0px 2px #3D3D3D",
                        cursor: "pointer !important",
                      },
                      "& .MuiSvgIcon-root": {
                        color: "white",
                      },
                      "& .MuiTablePagination-actions": {
                        display: "flex",
                        gap: "12px !important",
                        flex: 1,
                        justifyContent: "flex-end",
                      },
                      "&.MuiTablePagination-root": {
                        width: "100%",
                        flex: 1,
                        display: "flex",
                        justifyContent: "space-between",
                      },
                      "& .MuiTablePagination-spacer": {
                        flex: 0,
                        display: "none",
                      },
                      "& .MuiTablePagination-toolbar": {
                        flex: 1,
                        display: "flex",
                        paddingLeft: 0,
                      },
                      "& .MuiTablePagination-select": {
                        backgroundColor: "rgba(255, 255, 255, 0.08)",
                        borderRadius: "4px",
                        padding: "6px 12px",
                      },
                      "& .MuiInputBase-input": {
                        color: "#ffff",
                      },
                      // To hide the rows per page dropdown
                      "& .MuiInputBase-root": {
                        // display: "none",
                      },
                    }}
                    rowsPerPageOptions={[25, 50, 100]}
                    component="div"
                    count={productList?.totalScreens} // changeLater
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </Box>

                {/* -------Multi select rows Action bar------- */}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 20,
                    left: "auto",
                    right: "auto",
                    width: "85%",
                    zIndex: 10,
                  }}
                >
                  <ActionCard
                    onNegativeAction={() => {
                      setIsDeleteDialogVisible(true);
                    }}
                    negativeButtonText={"Delete"}
                    headerText={actionCardProps?.title || "No items selected"}
                    isActionCardVisible={activeSelectedMenu}
                    onCloseButtonClick={() => {
                      setActiveSelectedMenu(false);
                      setSelectedRows([]);
                    }}
                    isNegativeButtonVisible={true}
                    isEditMultipleComponentsButtonLoading={false}
                    isEditMultipleComponentsButtonVisible={true}
                    editMultipleComponentsButtonText={"Edit Multiple screens"}
                    onEditMultipleComponentsButtonAction={() => {
                      handleEditMultipleScreens();
                    }}
                    isEditButtonLoading={false}
                    isEditButtonVisible={true}
                    editButtonText={"Edit"}
                    onEditButtonAction={() => {
                      handleEditScreens();
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </TabPanel>
          <TabPanel value={value} index={2}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                // overflowX: "scroll",
              }}
            >
              <Box
                sx={{
                  width: "85%",
                  paddingBottom: activeSelectedMenu ? "70px" : "0px",
                  height: "auto",
                  // overflowX: "scroll",
                }}
              >
                <LayoutTable
                  loading={productsLoading}
                  // rows={productList?.paginatedPages} // changeLater
                  rows={productList?.paginatedScreens} // changeLater
                  columns={columnItems}
                  handleRefreshData={handleRefreshData}
                  selectedRows={selectedRows}
                  setSelectedRows={setSelectedRows}
                  activeSelectedMenu={activeSelectedMenu}
                  handleSelectedMenu={(values) => {
                    handleChangeSelectedMenu(values);
                  }}
                  allData={allData}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  searchFilteredProductList={searchFilteredProductList}
                  search={search}
                  activeFilters={activeFilters}
                  status={status}
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <TablePagination
                    labelRowsPerPage="Showing"
                    SelectProps={{
                      inputProps: {
                        "aria-label": "rows per page",
                      },
                      style: {
                        fontWeight: "700",
                      },
                      MenuProps: {
                        PaperProps: {
                          sx: {
                            backgroundColor: "#3D3D3D",
                            "& .MuiMenuItem-root": {
                              color: "#ffff",
                              "&:hover": {
                                backgroundColor: "#555555",
                              },
                            },
                          },
                        },
                      },
                    }}
                    sx={{
                      "& .MuiTablePagination-selectLabel": {
                        fontWeight: "700",
                        color: "rgba(255, 255, 255, 0.24)",
                        marginRight: "5px",
                      },
                      "& .MuiTablePagination-displayedRows": {
                        color: "rgba(255, 255, 255, 0.24)",
                        fontWeight: "600",
                      },
                      "& .MuiButtonBase-root": {
                        boxShadow: "inset 0px 0px 0px 2px #3D3D3D",
                        cursor: "pointer !important",
                      },
                      "& .MuiSvgIcon-root": {
                        color: "white",
                      },
                      "& .MuiTablePagination-actions": {
                        display: "flex",
                        gap: "12px !important",
                        flex: 1,
                        justifyContent: "flex-end",
                      },
                      "&.MuiTablePagination-root": {
                        width: "100%",
                        flex: 1,
                        display: "flex",
                        justifyContent: "space-between",
                      },
                      "& .MuiTablePagination-spacer": {
                        flex: 0,
                        display: "none",
                      },
                      "& .MuiTablePagination-toolbar": {
                        flex: 1,
                        display: "flex",
                        paddingLeft: 0,
                      },
                      "& .MuiTablePagination-select": {
                        backgroundColor: "rgba(255, 255, 255, 0.08)",
                        borderRadius: "4px",
                        padding: "6px 12px",
                      },
                      "& .MuiInputBase-input": {
                        color: "#ffff",
                      },
                      // To hide the rows per page dropdown
                      "& .MuiInputBase-root": {
                        // display: "none",
                      },
                    }}
                    rowsPerPageOptions={[25, 50, 100]}
                    component="div"
                    count={productList?.totalScreens} // changeLater
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </Box>

                {/* -------Multi select rows Action bar------- */}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 20,
                    left: "auto",
                    right: "auto",
                    width: "85%",
                    zIndex: 10,
                  }}
                >
                  <ActionCard
                    onNegativeAction={() => {
                      setIsDeleteDialogVisible(true);
                    }}
                    negativeButtonText={"Delete"}
                    headerText={actionCardProps?.title || "No items selected"}
                    isActionCardVisible={activeSelectedMenu}
                    onCloseButtonClick={() => {
                      setActiveSelectedMenu(false);
                      setSelectedRows([]);
                    }}
                    isNegativeButtonVisible={true}
                    isEditMultipleComponentsButtonLoading={false}
                    isEditMultipleComponentsButtonVisible={true}
                    editMultipleComponentsButtonText={"Edit Multiple screens"}
                    onEditMultipleComponentsButtonAction={() => {
                      handleEditMultipleScreens();
                    }}
                    isEditButtonLoading={false}
                    isEditButtonVisible={true}
                    editButtonText={"Edit"}
                    onEditButtonAction={() => {
                      handleEditScreens();
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </TabPanel>
          <TabPanel value={value} index={3}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                // overflowX: "scroll",
              }}
            >
              <Box
                sx={{
                  width: "85%",
                  paddingBottom: activeSelectedMenu ? "70px" : "0px",
                  height: "auto",
                  // overflowX: "scroll",
                }}
              >
                <LayoutTable
                  loading={productsLoading}
                  // rows={productList?.paginatedPages} // changeLater
                  rows={productList?.paginatedScreens} // changeLater
                  columns={columnItems}
                  handleRefreshData={handleRefreshData}
                  selectedRows={selectedRows}
                  setSelectedRows={setSelectedRows}
                  activeSelectedMenu={activeSelectedMenu}
                  handleSelectedMenu={(values) => {
                    handleChangeSelectedMenu(values);
                  }}
                  allData={allData}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  searchFilteredProductList={searchFilteredProductList}
                  search={search}
                  activeFilters={activeFilters}
                  status={status}
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <TablePagination
                    labelRowsPerPage="Showing"
                    SelectProps={{
                      inputProps: {
                        "aria-label": "rows per page",
                      },
                      style: {
                        fontWeight: "700",
                      },
                      MenuProps: {
                        PaperProps: {
                          sx: {
                            backgroundColor: "#3D3D3D",
                            "& .MuiMenuItem-root": {
                              color: "#ffff",
                              "&:hover": {
                                backgroundColor: "#555555",
                              },
                            },
                          },
                        },
                      },
                    }}
                    sx={{
                      "& .MuiTablePagination-selectLabel": {
                        fontWeight: "700",
                        color: "rgba(255, 255, 255, 0.24)",
                        marginRight: "5px",
                      },
                      "& .MuiTablePagination-displayedRows": {
                        color: "rgba(255, 255, 255, 0.24)",
                        fontWeight: "600",
                      },
                      "& .MuiButtonBase-root": {
                        boxShadow: "inset 0px 0px 0px 2px #3D3D3D",
                        cursor: "pointer !important",
                      },
                      "& .MuiSvgIcon-root": {
                        color: "white",
                      },
                      "& .MuiTablePagination-actions": {
                        display: "flex",
                        gap: "12px !important",
                        flex: 1,
                        justifyContent: "flex-end",
                      },
                      "&.MuiTablePagination-root": {
                        width: "100%",
                        flex: 1,
                        display: "flex",
                        justifyContent: "space-between",
                      },
                      "& .MuiTablePagination-spacer": {
                        flex: 0,
                        display: "none",
                      },
                      "& .MuiTablePagination-toolbar": {
                        flex: 1,
                        display: "flex",
                        paddingLeft: 0,
                      },
                      "& .MuiTablePagination-select": {
                        backgroundColor: "rgba(255, 255, 255, 0.08)",
                        borderRadius: "4px",
                        padding: "6px 12px",
                      },
                      "& .MuiInputBase-input": {
                        color: "#ffff",
                      },
                      // To hide the rows per page dropdown
                      "& .MuiInputBase-root": {
                        // display: "none",
                      },
                    }}
                    rowsPerPageOptions={[25, 50, 100]}
                    component="div"
                    count={productList?.totalScreens} // changeLater
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </Box>

                {/* -------Multi select rows Action bar------- */}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 20,
                    left: "auto",
                    right: "auto",
                    width: "85%",
                    zIndex: 10,
                  }}
                >
                  <ActionCard
                    onNegativeAction={() => {
                      setIsDeleteDialogVisible(true);
                    }}
                    negativeButtonText={"Delete"}
                    headerText={actionCardProps?.title || "No items selected"}
                    isActionCardVisible={activeSelectedMenu}
                    onCloseButtonClick={() => {
                      setActiveSelectedMenu(false);
                      setSelectedRows([]);
                    }}
                    isNegativeButtonVisible={true}
                    isEditMultipleComponentsButtonLoading={false}
                    isEditMultipleComponentsButtonVisible={true}
                    editMultipleComponentsButtonText={"Edit Multiple screens"}
                    onEditMultipleComponentsButtonAction={() => {
                      handleEditMultipleScreens();
                    }}
                    isEditButtonLoading={false}
                    isEditButtonVisible={true}
                    editButtonText={"Edit"}
                    onEditButtonAction={() => {
                      handleEditScreens();
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </TabPanel>
        </Box>
      </Box>
    </>
  );
};

export default ScreensList;

export interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

export function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      sx={{ width: "100%" }}
      className="HideScrollbar"
      // sx={{ maxHeight: SECTION_MIN_HEIGHT }}
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ flex: 1 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </Box>
  );
}
